/**
 * 라이트/다크 (UI-D2 (a) — 시스템 연동 + 수동 토글 + 선택 저장).
 *
 * 규칙 셋(DESIGN §2.6):
 *  ① 초기값 = 저장된 선택 > `prefers-color-scheme` > light.
 *  ② **저장값이 있는 동안 시스템 변경은 무시한다** — 사용자가 명시적으로 고른 것을
 *     OS 설정이 뒤엎으면 토글이 고장난 것처럼 보인다.
 *  ③ 스토리지 실패(사생활 모드·아티팩트 환경)는 오류가 아니다 — 감지만으로 성립해야 한다.
 *
 * 첫 페인트의 `data-theme` 는 index.html 인라인 스크립트가 이미 박아 뒀다. 여기서는 그 값을
 * 읽어 시작한다 — 다시 계산하면 두 곳의 판단이 갈라질 수 있다.
 */
import { useCallback, useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'elaine.theme'

function readStored(): Theme | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved === 'light' || saved === 'dark' ? saved : null
  } catch {
    return null
  }
}

function systemTheme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** index.html 이 박아 둔 값 → 저장값 → 시스템 순. */
export function initialTheme(): Theme {
  const painted = document.documentElement.dataset.theme
  if (painted === 'light' || painted === 'dark') return painted
  return readStored() ?? systemTheme()
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(initialTheme)

  useEffect(() => {
    document.documentElement.dataset.theme = theme
  }, [theme])

  // 저장된 선택이 없을 때만 시스템을 따라간다(규칙 ②).
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mq) return
    const onChange = (event: MediaQueryListEvent) => {
      if (readStored()) return
      setTheme(event.matches ? 'dark' : 'light')
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'light' ? 'dark' : 'light'
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // 저장 못 해도 이번 세션의 전환은 유효하다 — 막지 않는다.
      }
      return next
    })
  }, [])

  return { theme, toggle }
}
